import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { isNativeApp } from '../api/axios';
import AppShell from '../components/AppShell';
import { isLocalInspectionId, newClientUuid } from '../offline/drafts';
import { isOfflineMode } from '../offline/network';
import { rememberServerCode } from '../offline/inspectionCode';
import { useAppMode } from '../offline/useOffline';

const lookupTypes = [
  'engine_size', 'warranty_status', 'transmission', 'keys_count', 'fuel_type',
  'cylinders', 'engine_condition', 'drive_type', 'gear_type', 'body_shape',
  'seat_type', 'seats_count', 'exterior_color', 'interior_color',
];

function todayIsoDate() {
  const now = new Date();
  const pad = (num) => String(num).padStart(2, '0');
  return [now.getFullYear(), pad(now.getMonth() + 1), pad(now.getDate())].join('-');
}

const INITIAL_FORM = {
  inspection_date: '',
  owner_name: '',
  owner_phone: '',
  manufacturer_id: '',
  category_id: '',
  model_id: '',
  vin: '',
  plate_number: '',
  plate_code: '',
  odometer: '',
  engine_size: '',
  warranty_status: '',
  transmission: '',
  keys_count: '',
  fuel_type: '',
  cylinders: '',
  engine_condition: '',
  drive_type: '',
  gear_type: '',
  body_shape: '',
  seat_type: '',
  seats_count: '',
  exterior_color: '',
  interior_color: '',
};

// نداء واحد لكل القوائم ثم تجميعها محليًا بدل 14 طلب منفصل لكل نوع.
function buildLookupsMap(rows) {
  const nextLookups = {};
  lookupTypes.forEach((type) => {
    nextLookups[type] = [];
  });

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!nextLookups[row?.type]) return;
    nextLookups[row.type].push(row);
  });

  return nextLookups;
}

function buildOwnerName(owner) {
  const name = String(owner?.name || '').trim();
  return name === 'غير محدد' ? '' : name;
}

function buildInspectionForm(data) {
  const vehicle = data?.vehicle || {};

  return {
    ...INITIAL_FORM,
    inspection_date: data?.inspection_date || '',
    owner_name: buildOwnerName(vehicle?.owner),
    owner_phone: vehicle?.owner?.phone || '',
    manufacturer_id: vehicle?.manufacturer_id ? String(vehicle.manufacturer_id) : '',
    category_id: vehicle?.category_id ? String(vehicle.category_id) : '',
    model_id: vehicle?.model_id ? String(vehicle.model_id) : '',
    vin: vehicle?.vin || '',
    plate_number: vehicle?.plate_number || '',
    plate_code: vehicle?.plate_code || '',
    odometer: vehicle?.odometer ? String(vehicle.odometer) : '',
    engine_size: vehicle?.engine_size || '',
    warranty_status: vehicle?.warranty_status || '',
    transmission: vehicle?.transmission || '',
    keys_count: vehicle?.keys_count || '',
    fuel_type: vehicle?.fuel_type || '',
    cylinders: vehicle?.cylinders || '',
    engine_condition: vehicle?.engine_condition || '',
    drive_type: vehicle?.drive_type || '',
    gear_type: vehicle?.gear_type || '',
    body_shape: vehicle?.body_shape || '',
    seat_type: vehicle?.seat_type || '',
    seats_count: vehicle?.seats_count || '',
    exterior_color: vehicle?.exterior_color || '',
    interior_color: vehicle?.interior_color || '',
  };
}

export default function NewInspection() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [manufacturers, setManufacturers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [models, setModels] = useState([]);
  const [lookups, setLookups] = useState({});
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [displayNumber, setDisplayNumber] = useState(id);

  const [form, setForm] = useState(INITIAL_FORM);

  const errorText = (err) => err?.response?.data?.message || err?.userMessage || 'تعذّر تحميل أو حفظ بيانات الفحص.';
  const { mode } = useAppMode();
  const offline = mode === 'offline';
  const isLocalDraft = isLocalInspectionId(id);

  useEffect(() => {
    let mounted = true;

    const loadPageData = async () => {
      if (isEdit) {
        setLoading(true);
      }

      setError('');

      try {
        const baseRequests = [
          api.get('/manufacturers'),
          api.get('/models'),
          api.get('/lookups'),
        ];

        if (isEdit) {
          baseRequests.push(api.get(`/inspections/${id}`));
        }

        const [manufacturersRes, modelsRes, lookupsRes, inspectionRes] = await Promise.all(baseRequests);
        if (!mounted) return;

        setManufacturers(manufacturersRes.data || []);
        setModels(modelsRes.data || []);
        setLookups(buildLookupsMap(lookupsRes.data || []));

        if (!isEdit) {
          setForm({ ...INITIAL_FORM, inspection_date: todayIsoDate() });
          setCategories([]);
          return;
        }

        const inspectionData = inspectionRes?.data;
        setDisplayNumber(inspectionData?.display_number || inspectionData?.branch_serial || id);
        const nextForm = buildInspectionForm(inspectionData);

        setForm(nextForm);

        const [categoriesRes] = await Promise.all([
          nextForm.manufacturer_id
            ? api.get(`/categories/by-manufacturer/${nextForm.manufacturer_id}`)
            : Promise.resolve({ data: [] }),
        ]);

        if (!mounted) return;

        setCategories(categoriesRes.data || []);
      } catch (err) {
        if (!mounted) return;
        setError(errorText(err));
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadPageData();

    return () => {
      mounted = false;
    };
  }, [id, isEdit]);

  useEffect(() => {
    let mounted = true;

    const loadCategories = async () => {
      if (!form.manufacturer_id) {
        setCategories([]);
        return;
      }

      try {
        const { data } = await api.get(`/categories/by-manufacturer/${form.manufacturer_id}`);
        if (mounted) {
          setCategories(data || []);
        }
      } catch {
        if (mounted) {
          setCategories([]);
        }
      }
    };

    loadCategories();

    return () => {
      mounted = false;
    };
  }, [form.manufacturer_id]);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setManufacturer = (value) => setForm((s) => ({ ...s, manufacturer_id: value, category_id: '' }));
  const setCategory = (value) => setForm((s) => ({ ...s, category_id: value }));

  // الوضع أوفلاين: الفحص يُنشأ على الجهاز برقمه المركّب ويُرفع بالمزامنة.
  // الوضع أونلاين: في الخادم فقط؛ إن انقطع الاتصال لا يُحفظ على الجهاز بل نطلب اختيار «أوفلاين».
  const createInspection = async () => {
    const payload = { ...form, client_uuid: newClientUuid() };
    if (isOfflineMode()) {
      return api.post('/inspections', payload, { offlineCreate: true });
    }
    try {
      const response = await api.post('/inspections', payload);
      if (isNativeApp()) {
        rememberServerCode(response.data?.inspection_code, response.data?.branch_id, response.data?.inspector_id);
      }
      return response;
    } catch (err) {
      if (!err?.response && isNativeApp()) {
        err.userMessage = 'لا يوجد اتصال بالخادم. لإضافة الفحص على الجهاز اختر الوضع «أوفلاين».';
      }
      throw err;
    }
  };

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const { data } = isEdit
        ? await api.put(`/inspections/${id}`, form)
        : await createInspection();

      navigate(`/inspection/${data.id}/menu`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const selectField = (key, label, options) => (
    <label className="block">
      <span className="text-sm text-gray-600">{label}</span>
      <select className="mt-1 w-full rounded-lg border p-2" value={form[key] || ''} onChange={(e) => setField(key, e.target.value)}>
        <option value="">اختر</option>
        {(options || []).map((o) => (
          <option key={o.id || o.value} value={o.value}>{o.value}</option>
        ))}
      </select>
    </label>
  );

  return (
    <AppShell title={isEdit ? `تعديل بيانات الفحص #${displayNumber || id}` : 'فحص جديد'}>
      {loading && (
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
          جاري تحميل بيانات الفحص...
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {((!isEdit && offline) || isLocalDraft) && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {isLocalDraft
            ? 'هذا الفحص محفوظ على الجهاز ولم يُرفع بعد. التعديلات تُحفظ على الجهاز وتُرفع مع المزامنة.'
            : 'الوضع أوفلاين: سيُحفظ الفحص على هذا الجهاز برقمه، ويُرفع عند المزامنة بعد اختيار «أونلاين».'}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 bg-white rounded-2xl border p-4">
        <label className="block md:col-span-2">
          <span className="text-sm text-gray-600">تاريخ الفحص</span>
          <input
            type="date"
            className="mt-1 w-full rounded-lg border p-2"
            value={form.inspection_date}
            onChange={(e) => setField('inspection_date', e.target.value)}
          />
          <span className="mt-1 block text-xs text-gray-500">هو التاريخ الظاهر في قائمة الفحوصات والمستخدم في الفلترة بالتاريخ.</span>
        </label>

        <label className="block">
          <span className="text-sm text-gray-600">اسم المالك <span className="text-gray-400">(اختياري)</span></span>
          <input
            className="mt-1 w-full rounded-lg border p-2"
            value={form.owner_name}
            onChange={(e) => setField('owner_name', e.target.value)}
            placeholder="اسم مالك المركبة"
          />
        </label>

        <label className="block">
          <span className="text-sm text-gray-600">رقم جوال المالك <span className="text-gray-400">(اختياري)</span></span>
          <input
            inputMode="tel"
            dir="ltr"
            className="mt-1 w-full rounded-lg border p-2 text-right"
            value={form.owner_phone}
            onChange={(e) => setField('owner_phone', e.target.value)}
            placeholder="05xxxxxxxx"
          />
          <span className="mt-1 block text-xs text-gray-500">يُستخدم للبحث عن الفحص برقم الجوال من القائمة الرئيسية.</span>
        </label>

        <label className="block">
          <span className="text-sm text-gray-600">الشركة</span>
          <select className="mt-1 w-full rounded-lg border p-2" value={form.manufacturer_id} onChange={(e) => setManufacturer(e.target.value)}>
            <option value="">اختر</option>
            {manufacturers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="text-sm text-gray-600">الفئة</span>
          <select className="mt-1 w-full rounded-lg border p-2" value={form.category_id} onChange={(e) => setCategory(e.target.value)}>
            <option value="">اختر</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="text-sm text-gray-600">الموديل</span>
          <select className="mt-1 w-full rounded-lg border p-2" value={form.model_id} onChange={(e) => setField('model_id', e.target.value)}>
            <option value="">اختر</option>
            {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="text-sm text-gray-600">VIN</span>
          <input className="mt-1 w-full rounded-lg border p-2" value={form.vin} onChange={(e) => setField('vin', e.target.value)} />
        </label>

        <label className="block">
          <span className="text-sm text-gray-600">رقم لوحة السيارة</span>
          <input
            inputMode="numeric"
            className="mt-1 w-full rounded-lg border p-2"
            value={form.plate_number}
            onChange={(e) => setField('plate_number', e.target.value)}
            placeholder="4970"
          />
        </label>

        <label className="block">
          <span className="text-sm text-gray-600">رمز لوحة السيارة</span>
          <input
            className="mt-1 w-full rounded-lg border p-2"
            value={form.plate_code}
            onChange={(e) => setField('plate_code', e.target.value)}
            placeholder="ب ح ا"
            dir="rtl"
          />
        </label>

        <label className="block">
          <span className="text-sm text-gray-600">رقم العداد</span>
          <input type="number" className="mt-1 w-full rounded-lg border p-2" value={form.odometer} onChange={(e) => setField('odometer', e.target.value)} />
        </label>

        {selectField('engine_size', 'سعة المحرك', lookups.engine_size)}
        {selectField('warranty_status', 'حالة الضمان', lookups.warranty_status)}
        {selectField('transmission', 'نوع ناقل الحركة', lookups.transmission)}
        {selectField('keys_count', 'عدد المفاتيح', lookups.keys_count)}
        {selectField('fuel_type', 'نوع الوقود', lookups.fuel_type)}
        {selectField('cylinders', 'عدد السلندرات', lookups.cylinders)}
        {selectField('engine_condition', 'حالة المحرك', lookups.engine_condition)}
        {selectField('drive_type', 'نوع الدفع', lookups.drive_type)}
        {selectField('gear_type', 'نوع القير', lookups.gear_type)}
        {selectField('body_shape', 'شكل الهيكل', lookups.body_shape)}
        {selectField('seat_type', 'نوع المقاعد', lookups.seat_type)}
        {selectField('seats_count', 'عدد المقاعد', lookups.seats_count)}
        {selectField('exterior_color', 'اللون الخارجي', lookups.exterior_color)}
        {selectField('interior_color', 'اللون الداخلي', lookups.interior_color)}
      </div>

      <div className="mt-4">
        <button
          onClick={submit}
          disabled={saving || loading}
          className="rounded-xl bg-primary px-6 py-3 text-white font-bold disabled:opacity-50"
        >
          {saving ? 'جاري الحفظ...' : isEdit ? 'حفظ التعديلات' : 'التالي'}
        </button>
      </div>
    </AppShell>
  );
}
